import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Search, Filter, CalendarDays, BadgeCheck, XCircle, Eye, Trash2 } from 'lucide-react';
import AdminEventDetailsModal from './AdminEventDetailsModal';

type MovieRow = { id: number; title: string; organizer_id?: number; is_canceled?: boolean };
type OrganizerRow = { id: number; name: string };
type ScreeningRow = { id: number; movie_id: number; start_time: string; location: string };

export default function AdminManageEvents() {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'completed' | 'cancelled'>('all');
  const [organizerId, setOrganizerId] = useState<number | ''>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detailsMovieId, setDetailsMovieId] = useState<number | null>(null);
  const [organizers, setOrganizers] = useState<OrganizerRow[]>([]);
  const [movies, setMovies] = useState<MovieRow[]>([]);
  const [screeningsByMovie, setScreeningsByMovie] = useState<Record<number, ScreeningRow[]>>({});
  const [organizerSearch, setOrganizerSearch] = useState('');

  const searchEvents = async () => {
    try {
      let q = supabase
        .from('movies')
        .select('id,title,organizer_id,is_canceled', { count: 'exact' })

      if (query) q = q.ilike('title', `%${query}%`)
      if (organizerId) q = q.eq('organizer_id', organizerId)

      q = q.order('created_at', { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1)
      const { data: movieRows } = await q;
      const mv = ((movieRows || []) as MovieRow[]);
      setMovies(mv);
      if (mv.length) {
        const ids = mv.map(m => m.id);
        const { data: scr } = await supabase
          .from('screenings')
          .select('id,movie_id,start_time,location')
          .in('movie_id', ids)
          .order('start_time', { ascending: true });
        const by: Record<number, ScreeningRow[]> = {};
        for (const s of ((scr || []) as ScreeningRow[])) {
          by[s.movie_id] = by[s.movie_id] || [];
          by[s.movie_id].push(s);
        }
        setScreeningsByMovie(by);
      } else {
        setScreeningsByMovie({});
      }
    } catch {
      toast.error('Failed to search events');
    }
  };

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('organizers').select('id,name').order('name');
      setOrganizers(((data || []) as OrganizerRow[]));
    })();
  }, []);

  useEffect(() => { searchEvents(); }, [query, organizerId, page]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const df = dateFrom ? new Date(dateFrom).getTime() : null;
    const dt = dateTo ? new Date(dateTo).getTime() : null;
    return movies.filter(m => {
      const scrs = screeningsByMovie[m.id] || [];
      const upcoming = scrs.find(s => new Date(s.start_time).getTime() > now);
      const latest = scrs[scrs.length - 1] || null;
      let st: 'active' | 'completed' | 'cancelled' = 'completed';
      if (m.is_canceled) st = 'cancelled';
      else if (upcoming) st = 'active';
      else st = 'completed';
      if (status !== 'all' && st !== status) return false;
      if (df || dt) {
        const first = upcoming || latest;
        const ts = first ? new Date(first.start_time).getTime() : null;
        if (df && ts && ts < df) return false;
        if (dt && ts && ts > dt) return false;
      }
      return true;
    });
  }, [movies, screeningsByMovie, status, dateFrom, dateTo]);

  const filteredOrganizers = useMemo(() => {
    const s = organizerSearch.trim().toLowerCase();
    if (!s) return organizers;
    return organizers.filter(o => o.name.toLowerCase().includes(s));
  }, [organizers, organizerSearch]);



  const deleteEvent = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this event? This action will permanently remove all associated data (screenings, bookings, seats) and cannot be undone.')) {
      return;
    }
    try {
      const { error } = await supabase.rpc('delete_movie_complete', { target_movie_id: id });
      if (error) {
        console.error('Delete error:', error);
        toast.error('Failed to delete event');
        return;
      }
      setMovies(prev => prev.filter(m => m.id !== id));
      toast.success('Event and all data deleted successfully');
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete event');
    }
  };

  return (
    <div id="manage-events" className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">Manage Events</h3>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
        <div className="relative w-full md:w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search events..."
            className="pl-10 w-full rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3 placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <Filter className="text-gray-500 dark:text-gray-300 h-4 w-4" />
            <select value={status} onChange={e => setStatus(e.target.value as any)} className="w-full md:w-48 rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3">
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <input
              value={organizerSearch}
              onChange={e => setOrganizerSearch(e.target.value)}
              placeholder="Search organizers"
              className="w-full md:w-56 rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3 placeholder:text-gray-500 dark:placeholder:text-gray-400"
            />
            <select value={organizerId} onChange={e => { const v = e.target.value ? Number(e.target.value) : ''; setOrganizerId(v); setPage(1); }} className="w-full md:w-56 rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3">
              <option value="">All organizers</option>
              {filteredOrganizers.map(o => (<option key={o.id} value={o.id}>{o.name}</option>))}
            </select>
          </div>
        </div>
        <div className="flex justify-end md:justify-start">
          <button
            onClick={() => setFiltersOpen(v => !v)}
            aria-expanded={filtersOpen}
            className="px-3 py-2 bg-gray-200 text-gray-900 rounded flex items-center gap-2 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </button>
        </div>
      </div>
      {filtersOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Filters</h2>
              <button onClick={() => setFiltersOpen(false)} className="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">Close</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="col-span-1">
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Start date</label>
                <div className="flex items-center gap-2">
                  <CalendarDays className="text-gray-700 dark:text-gray-300 h-4 w-4" />
                  <input type="datetime-local" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3" />
                </div>
              </div>
              <div className="col-span-1">
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">End date</label>
                <div className="flex items-center gap-2">
                  <CalendarDays className="text-gray-700 dark:text-gray-300 h-4 w-4" />
                  <input type="datetime-local" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setFiltersOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600">Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <div className="mt-4 space-y-3">
        {filtered.map(m => {
          const scrs = screeningsByMovie[m.id] || [];
          const now = Date.now();
          const upcoming = scrs.find(s => new Date(s.start_time).getTime() > now);
          const latest = scrs[scrs.length - 1] || null;
          const st = m.is_canceled ? 'cancelled' : upcoming ? 'active' : 'completed';
          const badge = st === 'active' ? <BadgeCheck className="text-green-500 h-4 w-4" /> : st === 'cancelled' ? <XCircle className="text-red-500 h-4 w-4" /> : <BadgeCheck className="text-gray-400 h-4 w-4" />;
          const first = upcoming || latest;
          return (
            <div key={m.id} className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex justify-between">
                <div>
                  <p className="text-gray-900 dark:text-white font-semibold flex items-center gap-2">{badge}{m.title}</p>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{first ? new Date(first.start_time).toLocaleString() : '—'} · {first?.location || '—'} · {st}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setDetailsMovieId(m.id)} className="px-3 py-2 bg-indigo-600 text-white rounded flex items-center gap-2"><Eye className="h-4 w-4" />Details</button>

                  <button onClick={() => deleteEvent(m.id)} className="px-3 py-2 bg-red-600 text-white rounded flex items-center gap-2"><Trash2 className="h-4 w-4" />Delete</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={() => setPage(p => Math.max(1, p - 1))} className="px-3 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white rounded">Prev</button>
        <button onClick={() => setPage(p => p + 1)} className="px-3 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white rounded">Next</button>
      </div>
      {detailsMovieId !== null && (
        <AdminEventDetailsModal movieId={detailsMovieId} onClose={() => setDetailsMovieId(null)} />
      )}
    </div>
  );
}
